import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { bulkAssignAppraisals } from "@/services/performanceService";
import { getCompanyMiscellaneousData } from "@/services/adminService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";

// An employee row's Select value when they follow the cycle's default template.
const USE_DEFAULT_TEMPLATE = "default";

// Employee rows mounted at a time. Each is a full Select, so mounting a whole
// company stalls the dialog, most of all on phones. Search reaches everyone.
const EMPLOYEE_ROW_BATCH = 50;

const buildInitialCycleForm = () => ({
  cycleName: "",
  cycleStartDate: new Date().toISOString().split("T")[0],
  cycleDeadline: "",
  selectedDepartment: "all",
  cycleTemplateId: "",
  employeeTemplateIds: {}, // { [employee id]: template id }, overrides cycleTemplateId
  employeeSearch: "",
  employeeRowLimit: EMPLOYEE_ROW_BATCH,
});

/**
 * One employee's template picker. Memoized so typing elsewhere in the form
 * doesn't re-render every row of a large cycle.
 */
const EmployeeTemplateRow = memo(function EmployeeTemplateRow({
  employee,
  templateId,
  defaultTemplateName,
  templates,
  showDepartment,
  onChange,
}) {
  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{employee.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {employee.id}
          {showDepartment ? ` · ${employee.department}` : ""}
        </p>
      </div>
      <Select
        value={templateId || (defaultTemplateName ? USE_DEFAULT_TEMPLATE : "")}
        onValueChange={(value) => onChange(employee.id, value)}
      >
        <SelectTrigger
          className="w-full sm:w-56 sm:shrink-0"
          data-testid={`cycle-employee-template-${employee.id}`}
        >
          <SelectValue placeholder="Select template..." />
        </SelectTrigger>
        <SelectContent>
          {defaultTemplateName && (
            <SelectItem value={USE_DEFAULT_TEMPLATE}>
              Default ({defaultTemplateName})
            </SelectItem>
          )}
          {templates.map((t) => (
            <SelectItem key={t._id || t.id} value={t._id || t.id}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});

/**
 * Self-contained "New Appraisal Cycle" dialog (trigger button + form). Owns its
 * own form state and fetches its own department list, so typing only re-renders
 * this component, not the host page.
 *
 * Everyone in a cycle gets the default template unless a different one is picked
 * for them in the employee list; the whole list is still one cycle.
 *
 * Props:
 *  - employeeList: [{ id, name, department }]
 *  - templates: [{ _id|id, name, type }]
 *  - onCreated: () => void — called after a cycle is created (e.g. to reload data)
 */
export default function AppraisalCycleDialog({
  employeeList = [],
  templates = [],
  onCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermission();
  const { canCreate } = useMemo(() => createCanHelpers(can), [can]);
  const canCreateAppraisal = useMemo(() => canCreate("performance_management"), [canCreate]);

  // Controlled when a parent passes `open`/`onOpenChange` (e.g. driven from the
  // Performance "Actions" dropdown); otherwise self-managed with its own trigger.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [cycleForm, setCycleForm] = useState(buildInitialCycleForm);
  const [departments, setDepartments] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);

  // Departments are a paid feature. Without it a cycle can only ever be
  // company-wide, so the selector is hidden and the list is never fetched.
  const { hasFeature } = useSubscription();
  const canUseDepartments = hasFeature("department_mapping");

  // Fetch departments for the department selector.
  useEffect(() => {
    if (!canUseDepartments) {
      setDepartments([]);
      return;
    }

    const fetchDepartments = async () => {
      try {
        const response = await getCompanyMiscellaneousData();
        if (response && response.departments) {
          setDepartments(response.departments);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, [canUseDepartments]);

  const updateCycleForm = (field, value) =>
    setCycleForm((prev) => ({ ...prev, [field]: value }));

  const setEmployeeTemplate = useCallback((employeeId, templateId) => {
    setCycleForm((prev) => {
      const employeeTemplateIds = { ...prev.employeeTemplateIds };
      if (templateId === USE_DEFAULT_TEMPLATE) {
        delete employeeTemplateIds[employeeId];
      } else {
        employeeTemplateIds[employeeId] = templateId;
      }
      return { ...prev, employeeTemplateIds };
    });
  }, []);

  const resetForm = () => setCycleForm(buildInitialCycleForm());

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) resetForm();
  };

  const appraisalTemplates = useMemo(
    () => (Array.isArray(templates) ? templates.filter((t) => t.type === "Appraisal") : []),
    [templates]
  );
  const defaultTemplateName = appraisalTemplates.find(
    (t) => (t._id || t.id) === cycleForm.cycleTemplateId
  )?.name;

  // Everyone the cycle will cover. The server never assigns someone their own
  // appraisal, so the admin's own record isn't offered here either.
  const targetEmployees = useMemo(
    () =>
      employeeList.filter(
        (e) =>
          String(e._id) !== String(user?.employee_id) &&
          (cycleForm.selectedDepartment === "all" ||
            e.department === cycleForm.selectedDepartment)
      ),
    [employeeList, user?.employee_id, cycleForm.selectedDepartment]
  );

  const visibleEmployees = useMemo(() => {
    const search = cycleForm.employeeSearch.trim().toLowerCase();
    if (!search) return targetEmployees;
    return targetEmployees.filter(
      (e) =>
        e.name.toLowerCase().includes(search) ||
        String(e.id).toLowerCase().includes(search)
    );
  }, [targetEmployees, cycleForm.employeeSearch]);

  const missingTemplateCount = cycleForm.cycleTemplateId
    ? 0
    : targetEmployees.filter((e) => !cycleForm.employeeTemplateIds[e.id]).length;

  const handleCreateAppraisalCycle = async () => {
    if (!canCreate("performance_management", true)) return;

    if (missingTemplateCount > 0) {
      toast.error("Please select an appraisal template for every employee");
      return;
    }
    if (!cycleForm.cycleStartDate) {
      toast.error("Start date is required");
      return;
    }
    if (!cycleForm.cycleDeadline) {
      toast.error("Deadline is required");
      return;
    }

    setBulkCreating(true);
    try {
      const employeeIds = targetEmployees.map((e) => e.id);
      if (employeeIds.length === 0) {
        toast.error("No employees found for the selected department");
        return;
      }

      const response = await bulkAssignAppraisals(
        employeeIds,
        cycleForm.cycleTemplateId || undefined,
        cycleForm.cycleName || `Appraisal Cycle - ${new Date().toLocaleDateString()}`,
        {
          startDate: cycleForm.cycleStartDate || new Date().toISOString(),
          deadline: cycleForm.cycleDeadline || undefined,
          department: cycleForm.selectedDepartment,
          templateAssignments: targetEmployees
            .filter((e) => cycleForm.employeeTemplateIds[e.id])
            .map((e) => ({
              employeeId: e.id,
              templateId: cycleForm.employeeTemplateIds[e.id],
            })),
        }
      );

      if (response?.data?.created > 0) {
        if (response?.data?.errors?.length > 0) {
          toast.info(
            `Created ${response.data.created} appraisals. ${response.data.errors.length} skipped.`
          );
        } else {
          toast.success(`Successfully created ${response.data.created} appraisals!`);
        }
      } else {
        toast.error("No appraisals created.");
      }

      setOpen(false);
      resetForm();
      onCreated?.();
    } catch (error) {
      console.error("Error creating appraisal cycle:", error);
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to create appraisal cycle"
      );
    } finally {
      setBulkCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            size="sm"
            className={`bg-primary hover:bg-primary/90 ${
              !canCreateAppraisal ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={!canCreateAppraisal}
            data-testid="new-cycle-btn"
          >
            <Plus className="mr-1.5 h-4 w-4" /> New Appraisal Cycle
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Appraisal Cycle</DialogTitle>
          <DialogDescription>
            Launch a company-wide or department-specific performance review process.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Cycle Name */}
          <div className="grid gap-2">
            <Label>Cycle Name</Label>
            <Input
              placeholder="e.g., Annual Review 2025"
              value={cycleForm.cycleName}
              onChange={(e) => updateCycleForm("cycleName", e.target.value)}
              data-testid="cycle-name-input"
            />
          </div>

          {/* Start Date & Deadline */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={cycleForm.cycleStartDate}
                onChange={(e) => updateCycleForm("cycleStartDate", e.target.value)}
                data-testid="cycle-start-date"
              />
            </div>
            <div className="grid gap-2">
              <Label>Deadline</Label>
              <Input
                type="date"
                value={cycleForm.cycleDeadline}
                onChange={(e) => updateCycleForm("cycleDeadline", e.target.value)}
                data-testid="cycle-deadline"
              />
            </div>
          </div>

          {/* Department Selection — omitted when the plan has no departments;
              the cycle then targets the whole company. */}
          {canUseDepartments && (
          <div className="grid gap-2">
            <Label>Select Departments</Label>
            <Select
              value={cycleForm.selectedDepartment}
              onValueChange={(value) => updateCycleForm("selectedDepartment", value)}
            >
              <SelectTrigger data-testid="cycle-department-select">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept._id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {/* Default Template Selection */}
          <div className="grid gap-2">
            <Label>Default Appraisal Template</Label>
            <Select
              value={cycleForm.cycleTemplateId}
              onValueChange={(value) => updateCycleForm("cycleTemplateId", value)}
            >
              <SelectTrigger data-testid="cycle-template-select">
                <SelectValue placeholder="Select template..." />
              </SelectTrigger>
              <SelectContent>
                {appraisalTemplates.length === 0 ? (
                  <div className="py-6 px-4 text-center">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                      No Appraisal templates available
                    </p>
                    <Button
                      variant="link"
                      className="text-xs h-auto p-0"
                      onClick={() => navigate("/template-builder")}
                    >
                      Create template →
                    </Button>
                  </div>
                ) : (
                  appraisalTemplates.map((t) => (
                    <SelectItem key={t._id || t.id} value={t._id || t.id}>
                      {t.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Applies to everyone below unless you choose a different template for them.
            </p>
          </div>

          {/* Per-employee Template Selection */}
          {appraisalTemplates.length > 0 && targetEmployees.length > 0 && (
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Employees ({targetEmployees.length})</Label>
                {missingTemplateCount > 0 && (
                  <span className="text-xs text-destructive" data-testid="cycle-missing-templates">
                    {missingTemplateCount} still need a template
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name or employee ID..."
                  value={cycleForm.employeeSearch}
                  onChange={(e) => updateCycleForm("employeeSearch", e.target.value)}
                  data-testid="cycle-employee-search"
                />
              </div>
              <div className="max-h-56 divide-y overflow-y-auto rounded-md border thin-scrollbar">
                {visibleEmployees.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No employees match your search
                  </p>
                ) : (
                  visibleEmployees.slice(0, cycleForm.employeeRowLimit).map((employee) => (
                    <EmployeeTemplateRow
                      key={employee.id}
                      employee={employee}
                      templateId={cycleForm.employeeTemplateIds[employee.id]}
                      defaultTemplateName={defaultTemplateName}
                      templates={appraisalTemplates}
                      showDepartment={canUseDepartments}
                      onChange={setEmployeeTemplate}
                    />
                  ))
                )}
                {visibleEmployees.length > cycleForm.employeeRowLimit && (
                  <div className="p-2 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        updateCycleForm(
                          "employeeRowLimit",
                          cycleForm.employeeRowLimit + EMPLOYEE_ROW_BATCH
                        )
                      }
                      data-testid="cycle-employee-show-more"
                    >
                      Show more ({visibleEmployees.length - cycleForm.employeeRowLimit} remaining)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            className="bg-primary hover:bg-primary/90"
            onClick={handleCreateAppraisalCycle}
            disabled={bulkCreating || appraisalTemplates.length === 0 || missingTemplateCount > 0}
            data-testid="cycle-create-btn"
          >
            {bulkCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Launch Cycle
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
